import PollResults from './PollResults';
import QuizLeaderboard from './QuizLeaderboard';
import WordCloudDisplay from './WordCloudDisplay';
import LiveResponseCounter from './LiveResponseCounter';

// Generic Text Responses Display Component
const TextResponsesDisplay = ({ activity, responses, responseKey = 'text' }) => {
  if (!responses || responses.length === 0) {
    return (
      <div className="text-center py-12 bg-neutral-50 rounded-lg">
        <p className="text-neutral-500">No responses yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-3 max-h-96 overflow-y-auto">
      {responses.map((response, index) => (
        <div key={index} className="p-4 bg-white border border-neutral-200 rounded-lg">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-sm font-medium text-primary-600">
                {response.participantName ? response.participantName.charAt(0).toUpperCase() : 'A'}
              </span>
            </div>
            <div className="flex-1">
              <p className="text-sm text-neutral-900">
                {response[responseKey] || response.answer || response.response || response.text || JSON.stringify(response)}
              </p>
              {response.participantName && (
                <p className="text-xs text-neutral-500 mt-1">{response.participantName}</p>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Feedback Results Display Component
const FeedbackResultsDisplay = ({ activity, responses }) => {
  if (!responses || responses.length === 0) {
    return (
      <div className="text-center py-12 bg-neutral-50 rounded-lg">
        <p className="text-neutral-500">No feedback yet</p>
      </div>
    );
  }

  // Calculate average rating if available
  const ratings = responses.filter(r => r.rating).map(r => r.rating);
  const avgRating = ratings.length > 0 ? (ratings.reduce((a, b) => a + b, 0) / ratings.length).toFixed(1) : null;

  return (
    <div className="space-y-4">
      {/* Average Rating Display */}
      {avgRating && (
        <div className="bg-primary-50 rounded-xl p-6 text-center mb-4">
          <p className="text-sm text-primary-600 font-medium mb-2">Average Rating</p>
          <p className="text-4xl font-bold text-primary-700">{avgRating}</p>
          <p className="text-sm text-primary-500 mt-1">out of 5</p>
        </div>
      )}

      {/* Individual Feedback */}
      <div className="space-y-3 max-h-80 overflow-y-auto">
        {responses.map((response, index) => (
          <div key={index} className="p-4 bg-white border border-neutral-200 rounded-lg">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                {response.rating && (
                  <div className="flex items-center gap-1 mb-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <svg
                        key={star}
                        className={`w-5 h-5 ${star <= response.rating ? 'text-yellow-400' : 'text-neutral-300'}`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                )}
                {response.comment && (
                  <p className="text-sm text-neutral-700">{response.comment}</p>
                )}
                {response.participantName && (
                  <p className="text-xs text-neutral-500 mt-2">{response.participantName}</p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Challenges/Action Plans Results Display Component
const ListResponsesDisplay = ({ activity, responses, itemKey = 'challenges', itemLabel = 'Items' }) => {
  if (!responses || responses.length === 0) {
    return (
      <div className="text-center py-12 bg-neutral-50 rounded-lg">
        <p className="text-neutral-500">No {itemLabel.toLowerCase()} submitted yet</p>
      </div>
    );
  }

  // Flatten all items from all responses. Responses arrive in several shapes:
  //   { challenges: [...], participantName }                              top-level array
  //   { actionPlans: [...], participantName }                             top-level array
  //   { text: '...', participantName }                                    legacy single-item
  //   { response: { challenges|actionPlans|text: ... }, participantName } wrapped from DB
  // Earlier the action-plan path used itemKey="actions" but the actual stored
  // field is "actionPlans" — so multi-plan submissions silently fell through
  // to response.text (the legacy first-plan-only field). The lookup now
  // checks all known keys defensively, including nested-under-response.
  const pickArray = (src) =>
    src?.[itemKey] || src?.actionPlans || src?.challenges || src?.items || src?.actions;

  const allItems = responses.flatMap((response) => {
    // 1) Array directly on response
    const directArr = pickArray(response);
    if (Array.isArray(directArr) && directArr.length > 0) {
      return directArr.map((item) => ({
        text: typeof item === 'string' ? item : (item.text || item.description || item.action || JSON.stringify(item)),
        participantName: response.participantName,
      }));
    }
    // 2) Array nested under response.response (DB-shaped row)
    const wrappedArr = pickArray(response.response);
    if (Array.isArray(wrappedArr) && wrappedArr.length > 0) {
      return wrappedArr.map((item) => ({
        text: typeof item === 'string' ? item : (item.text || item.description || item.action || JSON.stringify(item)),
        participantName: response.participantName,
      }));
    }
    // 3) Legacy single-text fields
    if (response.text) {
      return [{ text: response.text, participantName: response.participantName }];
    }
    if (response.response?.text) {
      return [{ text: response.response.text, participantName: response.participantName }];
    }
    return [];
  });

  return (
    <div className="space-y-3 max-h-96 overflow-y-auto">
      <p className="text-sm text-neutral-500 mb-3">
        {allItems.length} {itemLabel.toLowerCase()} from {responses.length} participants
      </p>
      {allItems.map((item, index) => (
        <div key={index} className="p-3 bg-white border border-neutral-200 rounded-lg flex items-start gap-3">
          <div className="w-6 h-6 bg-neutral-100 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-xs font-medium text-neutral-600">{index + 1}</span>
          </div>
          <div className="flex-1">
            <p className="text-sm text-neutral-900">{item.text}</p>
            {item.participantName && (
              <p className="text-xs text-neutral-500 mt-1">by {item.participantName}</p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * Activity Results Viewer Component
 * Unified component for displaying activity results with export functionality
 */

const ActivityResultsViewer = ({
  activity,
  responses = [],
  totalParticipants = 0,
}) => {
  // Render the appropriate results component based on activity type
  const renderResults = () => {
    if (!activity) {
      return (
        <div className="flex items-center justify-center h-64 bg-neutral-50 rounded-lg">
          <div className="text-center">
            <svg className="w-12 h-12 mx-auto text-neutral-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <p className="text-neutral-600">No activity selected</p>
          </div>
        </div>
      );
    }

    // Normalize activity type to lowercase for consistent matching
    const activityType = activity.type?.toLowerCase();

    switch (activityType) {
      case 'poll':
        return (
          <PollResults
            activity={activity}
            responses={responses}
          />
        );

      case 'quiz':
        return (
          <QuizLeaderboard
            activity={activity}
            responses={responses}
          />
        );

      case 'word_cloud':
      case 'wordcloud':
        return (
          <WordCloudDisplay
            activity={activity}
            responses={responses}
          />
        );

      case 'openquestion':
      case 'open_question':
        return (
          <TextResponsesDisplay
            activity={activity}
            responses={responses}
            responseKey="answer"
          />
        );

      case 'qa':
      case 'qna':
        return (
          <TextResponsesDisplay
            activity={activity}
            responses={responses}
            responseKey="question"
          />
        );

      case 'challenges':
        return (
          <ListResponsesDisplay
            activity={activity}
            responses={responses}
            itemKey="challenges"
            itemLabel="Challenges"
          />
        );

      case 'actionplans':
      case 'action_plans':
        return (
          <ListResponsesDisplay
            activity={activity}
            responses={responses}
            itemKey="actionPlans"
            itemLabel="Action Items"
          />
        );

      case 'feedback':
        return (
          <FeedbackResultsDisplay
            activity={activity}
            responses={responses}
          />
        );

      default:
        // Generic fallback for unknown types - show responses as text
        return (
          <TextResponsesDisplay
            activity={activity}
            responses={responses}
          />
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Response Counter */}
      <LiveResponseCounter
        totalParticipants={totalParticipants}
        responseCount={responses.length}
        activityType={activity?.type}
        showDetails={true}
      />

      {/* Results Display */}
      {renderResults()}
    </div>
  );
};

export default ActivityResultsViewer;
